import { Link } from "react-router-dom"
import { useSeoMeta } from "@unhead/react"
import {Sparkles, Heart, ShieldCheck, ArrowRight, BookOpen, Compass, SunMedium} from 'lucide-react'
import { brandName, images, location, practitionerName, profession, siteName } from "@/data/site"

function About() {
  useSeoMeta({
    title: `Qui suis-je ? · ${siteName}`,
    description: `${practitionerName}, ${profession.toLowerCase()} à ${location}. Découvrez mon histoire et ma vision de l'accompagnement périnatal.`,
    ogTitle: `Qui suis-je ? · ${siteName}`,
    ogDescription: `${practitionerName}, ${profession.toLowerCase()} à ${location}. Découvrez mon histoire et ma vision de l'accompagnement périnatal.`,
    ogImage: images.portrait,
    twitterCard: "summary_large_image",
  })

  return (
    <div className="text-[#2d221b]">
      {/* En-tête / Introduction */}
      <section className="mx-auto max-w-5xl px-5 pb-8 pt-14 text-center md:px-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#d6c4b6] bg-[#efe1d4]/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-[#b85c38]">
          <Heart size={14} />
          <span>Qui suis-je ?</span>
        </div>
        <h1 className="mt-4 font-display text-4xl font-medium leading-tight text-[#2d221b] md:text-6xl">
          Et si, au milieu de tout ça,
          <span className="block font-normal italic text-[#8a4225]">on prenait aussi soin de vous ?</span>
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-[#5e4b3e] md:text-lg">
          Je suis Maïwenn, doula et accompagnante périnatale.
        </p>
      </section>

      {/* Portrait et Texte principal */}
      <section className="mx-auto grid max-w-6xl gap-10 px-5 pb-16 md:grid-cols-[0.85fr_1.15fr] md:px-8">
        <div className="space-y-6">
          <div className="overflow-hidden rounded-3xl border border-[#dfd0c2] bg-white p-3 shadow-warm-xs">
            <img
              src={images.portrait}
              alt={`Portrait de ${practitionerName}, accompagnante périnatale`}
              loading="lazy"
              className="h-[480px] w-full rounded-2xl object-cover object-center"
            />
          </div>

          <div className="rounded-2xl border border-[#dfd0c2] bg-[#fbf7f2] p-6 shadow-warm-xs">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">Au creux de maman</span>
            <p className="mt-2 text-xs leading-relaxed text-[#5e4b3e] md:text-sm">
              {profession} à {location}, sous le nom <strong className="font-semibold text-[#2d221b]">{brandName}</strong>, je vous accueille et me déplace à votre rencontre pour vous entourer avec bienveillance, authenticité et douceur.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="overflow-hidden rounded-2xl border border-[#dfd0c2] bg-white p-2 shadow-warm-xs">
              <img
                src={images.aboutFamily}
                alt="Moment de douceur en famille"
                loading="lazy"
                className="h-36 w-full rounded-xl object-cover"
              />
            </div>
            <div className="overflow-hidden rounded-2xl border border-[#dfd0c2] bg-white p-2 shadow-warm-xs">
              <img
                src={images.aboutAtmosphere}
                alt="Ambiance cocon et douceur"
                loading="lazy"
                className="h-36 w-full rounded-xl object-cover"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {[
              ["Doula", "En formation Envol & Matrescence"],
              ["Massages", "Certifiée Graine de Massage"],
              ["Espace", "Inguiniel et ses alentours"],
              ["Écoute", "Safe place sans aucun jugement"],
            ].map(([label, value]) => (
              <div key={label} className="rounded-2xl border border-[#ebdcd0] bg-white/90 p-4 shadow-warm-xs">
                <p className="font-display text-xl font-bold text-[#8a4225]">{label}</p>
                <p className="mt-1 text-xs text-[#685548]">{value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Récit de présentation complet */}
        <div className="flex flex-col justify-between rounded-3xl border border-[#dfd0c2] bg-[#efe1d4] p-8 shadow-warm-xs md:p-12">
          <div className="space-y-6 text-[#4a392e] text-sm md:text-[15px] leading-relaxed">
            
            <div className="border-b border-[#ddcbbe] pb-5">
              <p className="font-medium text-[#2d221b] text-base md:text-lg">
                Je suis maman d'une petite fille née en août 2023 et d'un petit garçon né en mai 2025.
              </p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-wider text-[#8a4225]">
                <span className="rounded-full bg-white/80 px-3 py-1">Deux bébés</span>
                <span className="rounded-full bg-white/80 px-3 py-1">Deux naissances</span>
                <span className="rounded-full bg-white/80 px-3 py-1">Deux expériences</span>
              </div>
              <p className="mt-3 italic text-[#5e4b3e]">
                Deux expériences qui m'ont profondément transformée.
              </p>
            </div>

            <p>
              Devenir mère m'a ouvert un monde que je ne soupçonnais pas. Celui de la maternité, du post-partum, du lien, du bouleversement identitaire… mais aussi celui des injonctions, des doutes, de la fatigue et des tempêtes que l'on traverse parfois en silence.
            </p>

            <p className="font-display text-xl md:text-2xl font-medium text-[#8a4225]">
              Et c'est précisément là que j'ai envie d'être. À vos côtés.
            </p>

            {/* Écoute et accueil des émotions */}
            <div className="rounded-2xl border border-[#ebdcd0] bg-white/70 p-5 md:p-6 space-y-2.5">
              <p className="font-medium text-[#2d221b]">Vous n'avez pas besoin d'être une maman parfaite.</p>
              <p className="text-[#5e4b3e]">Vous avez besoin d'être écoutée.</p>
              <ul className="space-y-1.5 text-xs md:text-sm text-[#5e4b3e]">
                <li className="flex items-start gap-2">
                  <span className="text-[#b85c38] font-bold">•</span>
                  <span>D'être entendue sans qu'on vous réponde « c'est normal ».</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#b85c38] font-bold">•</span>
                  <span>D'être crue sans avoir à justifier ce que vous ressentez.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#b85c38] font-bold">•</span>
                  <span>D'avoir le droit d'être heureuse et épuisée.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#b85c38] font-bold">•</span>
                  <span>D'aimer profondément votre bébé et de trouver certains moments difficiles.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#b85c38] font-bold">•</span>
                  <span>D'avoir confiance et de douter.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#b85c38] font-bold">•</span>
                  <span>D'être mère sans cesser d'être femme.</span>
                </li>
              </ul>
            </div>

            <p>
              Je veux créer pour vous cet espace. Une <strong>safe place</strong>, sans jugement, sans injonction et sans « il faut ». Un endroit où vous pouvez déposer ce qui déborde, poser vos questions, parler de ce qui vous traverse et simplement être vous.
            </p>

            {/* Remettre la femme au centre du village */}
            <div className="border-t border-[#ddcbbe] pt-5">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">Vision</span>
              <h2 className="mt-1 font-display text-2xl font-medium text-[#2d221b] md:text-3xl">
                Remettre la femme au centre du village
              </h2>
              <p className="mt-3">
                On dit souvent qu'il faut tout un village pour élever un enfant. Moi, j'ai envie que dans ce village, on n'oublie pas la femme.
              </p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs md:text-sm text-[#5e4b3e]">
                <div className="rounded-xl bg-white/60 p-2.5">✨ Celle qui porte</div>
                <div className="rounded-xl bg-white/60 p-2.5">🌿 Celle qui donne naissance</div>
                <div className="rounded-xl bg-white/60 p-2.5">🤍 Celle qui devient mère</div>
                <div className="rounded-xl bg-white/60 p-2.5">🌾 Celle qui cherche sa place</div>
              </div>
              <p className="mt-2 text-xs italic text-[#5e4b3e]">
                Celle qui change, parfois sans même se reconnaître.
              </p>
              <p className="mt-3 font-medium text-[#2d221b]">
                Parce que prendre soin du bébé, c'est aussi prendre soin de celle qui s'en occupe.
              </p>
              <p className="mt-3 text-xs md:text-sm">
                Mon accompagnement repose sur des valeurs qui me sont essentielles : <strong>féminisme, bienveillance, écoute, entraide, compréhension et non-jugement</strong>.
              </p>
              <p className="mt-3 text-sm font-medium text-[#8a4225] italic">
                Je ne suis pas là pour vous apprendre à être mère. Je suis là pour vous accompagner à devenir la mère que vous avez envie d'être, sans vous perdre en chemin.
              </p>
            </div>

            {/* Une doula en chemin */}
            <div className="border-t border-[#ddcbbe] pt-5">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">Formations & parcours</span>
              <h2 className="mt-1 font-display text-2xl font-medium text-[#2d221b] md:text-3xl">
                Une doula en chemin
              </h2>
              <div className="mt-3 space-y-3">
                <p>
                  Je suis actuellement en formation de doula auprès d'<strong>Envol & Matrescence</strong>, une formation en présentiel qui s'étend sur plusieurs mois et que je terminerai en décembre.
                </p>
                <p>
                  Je suis également certifiée en <strong>réflexologie bébé émotionnelle</strong> et en <strong>massage bébé</strong> auprès de <strong>Graine de Massage</strong>.
                </p>
                <p className="text-xs text-[#5e4b3e] leading-relaxed">
                  Je continue à apprendre, à questionner mes pratiques et à enrichir mes connaissances, avec une conviction : accompagner l'intime demande de l'humilité, de l'écoute et beaucoup d'humanité.
                </p>
              </div>
            </div>

            {/* En dehors de la maternité */}
            <div className="border-t border-[#ddcbbe] pt-5">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">Au-delà</span>
              <h2 className="mt-1 font-display text-2xl font-medium text-[#2d221b] md:text-3xl">
                Et moi, en dehors de la maternité ?
              </h2>
              <p className="mt-3">
                Je suis aussi passionnée par les animaux, les chevaux et l'équitation. J'aime l'éducation canine, et tout ce qui touche de près ou de loin à la parentalité.
              </p>
              <p className="mt-2">
                Je crois beaucoup au lien, à la confiance et au respect du rythme de chacun.
              </p>
            </div>

            {/* Conclusion chaleureuse */}
            <div className="rounded-2xl border border-[#d8c4b2] bg-gradient-to-r from-[#f7ede3] to-[#ebdcd0] p-6 text-center space-y-2">
              <p className="font-display text-xl md:text-2xl font-medium text-[#2d221b]">
                Et finalement, c'est exactement ce que j'ai envie de vous offrir.
              </p>
              <p className="text-xs md:text-sm text-[#5e4b3e]">
                Un espace pour souffler · Une présence sur laquelle vous appuyer · Un accompagnement qui vous ressemble.
              </p>
              <p className="font-medium text-[#8a4225]">
                Parce que vous méritez, vous aussi, d'être entourée.
              </p>
              <p className="pt-2 font-display text-2xl md:text-3xl italic text-[#2d221b]">
                Bienvenue dans mon village.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Cadre et limites */}
      <section className="border-y border-[#3a281e] bg-[#2d2018] text-[#f7efe6]">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 py-16 md:grid-cols-2 md:px-8">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-[#e8a382]">
              <ShieldCheck size={16} />
              <span>Cadre Déontologique</span>
            </div>
            <h2 className="mt-3 font-display text-3xl font-medium md:text-4xl text-[#fdfaf7]">
              Ce que je fais, ce que je ne fais pas
            </h2>
            <p className="mt-4 text-xs leading-relaxed text-[#dfd2c6] md:text-sm">
              Une posture claire, transparente et rassurante pour vous accompagner en toute confiance.
            </p>
          </div>
          <div className="grid gap-6">
            <div className="rounded-2xl border border-[#533b2e] bg-[#3a2a20]/60 p-6">
              <h3 className="font-display text-2xl font-medium text-[#e8a382]">Ce que je fais</h3>
              <p className="mt-2 text-xs leading-relaxed text-[#dfd2c6] md:text-sm">
                Écoute sans jugement, transmission d'informations objectives, soutien physique et émotionnel non médical, préparation personnalisée du projet de naissance, présence rassurante pendant le travail et visites post-partum.
              </p>
            </div>
            <div className="rounded-2xl border border-[#533b2e] bg-[#3a2a20]/60 p-6">
              <h3 className="font-display text-2xl font-medium text-[#e8a382]">Ce que je ne fais pas</h3>
              <p className="mt-2 text-xs leading-relaxed text-[#dfd2c6] md:text-sm">
                Aucun acte médical, aucun diagnostic de santé, aucune substitution à la sage-femme ou au médecin. Je ne décide jamais à votre place.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Appel à l'action */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:px-8">
        <div className="rounded-3xl border border-[#dfd0c2] bg-gradient-to-br from-[#fbf7f2] to-[#efe1d4] p-8 shadow-warm-xs md:p-12">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-[#b85c38]" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">Rencontre</span>
          </div>
          <h2 className="mt-2 font-display text-3xl font-medium text-[#2d221b] md:text-4xl">Faisons connaissance</h2>
          <p className="mt-4 max-w-2xl text-xs leading-relaxed text-[#5e4b3e] md:text-sm">
            Vous avez envie d'échanger sur votre grossesse, vos souhaits ou votre post-partum ? Je serai ravie de vous écouter lors d'un premier temps de découverte.
          </p>
          <div className="mt-8">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#b85c38] to-[#9d4c2e] px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-white shadow-warm-sm transition-all hover:scale-[1.02] hover:shadow-warm-md"
            >
              <span>Me contacter</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default About
